import { IconButton, Button, Badge, Tooltip, Box, TextField } from '@mui/material'
import { Close } from '@mui/icons-material'
import { styled } from '@mui/material/styles'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import CameraAltIcon from '@mui/icons-material/CameraAlt'
import { useMemo, useEffect } from 'react'

export interface ImageWithComment {
  file: File
  comment: string
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
})

function ProtocolRoomImagesUpload({
  onAdd,
  onRemove,
  onCommentChange,
  images,
  disabled,
  singleImage,
  multiple
}: {
  roomId: number
  onAdd: (image: File | File[]) => void
  onRemove: (key: number) => void
  onCommentChange?: (key: number, comment: string) => void
  images: ImageWithComment[] | (File | string)[]
  disabled?: boolean
  singleImage?: boolean
  multiple?: boolean
}) {
  // Helper function to check if images are in the new format
  const isImageWithComment = (img: any): img is ImageWithComment => {
    return img && typeof img === 'object' && 'file' in img && 'comment' in img
  }

  const hasCommentSupport = onCommentChange !== undefined

  // Memoize image URLs to prevent multiple createObjectURL calls
  const imageData = useMemo(() => {
    return images.map((image) => {
      const isNewFormat = isImageWithComment(image)
      if (typeof image === 'string') {
        return {
          url: image,
          name: 'image',
          comment: '',
          isNewFormat: false,
          isString: true
        }
      }

      const file = isNewFormat ? image.file : (image as File)
      return {
        url: URL.createObjectURL(file),
        name: file.name,
        comment: isNewFormat ? image.comment : '',
        isNewFormat,
        isString: false
      }
    })
  }, [images])

  // Clean up object URLs when component unmounts or images change
  useEffect(() => {
    return () => {
      imageData.forEach((data) => {
        if (!data.isString && data.url) {
          URL.revokeObjectURL(data.url)
        }
      })
    }
  }, [imageData])

  return (
    <>
      <Box mb={2}>
        {images.map((_, key) => {
          const { url: imageUrl, name: imageName, comment } = imageData[key]

          return (
            <Box
              key={`${imageName}_${key}`}
              sx={{ mb: 2, display: 'inline-block', mx: 1, verticalAlign: 'top' }}
            >
              <Badge
                color='primary'
                sx={{
                  '.MuiBadge-badge': {
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%'
                  },
                  display: 'block'
                }}
                invisible={disabled}
                badgeContent={
                  <IconButton size='small' onClick={() => onRemove(key)}>
                    <Close fontSize='small' color='secondary' />
                  </IconButton>
                }
              >
                <Tooltip title={imageName} placement='top'>
                  <Box
                    sx={{
                      width: '96px',
                      height: '96px',
                      backgroundImage: `url("${imageUrl}")`,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.400',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: 'cover'
                    }}
                  />
                </Tooltip>
              </Badge>
              {hasCommentSupport && (
                <TextField
                  fullWidth
                  size='small'
                  placeholder='Komentarz (opcjonalnie)'
                  value={comment}
                  onChange={(e) => onCommentChange!(key, e.target.value)}
                  disabled={disabled}
                  sx={{ mt: 1, width: '96px' }}
                />
              )}
            </Box>
          )
        })}
      </Box>
      {!(singleImage && images.length) && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Button
            disabled={disabled}
            component='label'
            variant='contained'
            startIcon={<CameraAltIcon />}
            sx={{ display: { xs: 'flex', lg: 'none' }, mb: 2 }}
          >
            Zrób zdjęcie
            <VisuallyHiddenInput
              type='file'
              accept='image/*'
              capture='environment'
              multiple={multiple}
              onChange={(e: any) => {
                if (e.target.files.length) {
                  onAdd(multiple ? Array.from(e.target.files) : e.target.files[0])
                  return (e.target.value = '')
                }

                return (e.target.value = '')
              }}
            />
          </Button>
          <Button
            disabled={disabled}
            component='label'
            variant='contained'
            startIcon={<CloudUploadIcon />}
            sx={{ mb: 2 }}
          >
            Dodaj zdjęcie
            <VisuallyHiddenInput
              type='file'
              accept='image/*'
              multiple={multiple}
              onChange={(e: any) => {
                if (e.target.files.length) {
                  onAdd(multiple ? Array.from(e.target.files) : e.target.files[0])
                  return (e.target.value = '')
                }

                return (e.target.value = '')
              }}
            />
          </Button>
        </Box>
      )}
    </>
  )
}

export default ProtocolRoomImagesUpload
